<script setup>
import { ref, computed, watch } from 'vue';
import { useToast } from "@/composables/useToast.js";

const props = defineProps({
    show: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['close', 'regenerated']);

const { success, error, info, warning } = useToast();

const loading = ref(false);
const checkingStatus = ref(false);
const confirmText = ref('');
const acknowledgeRisks = ref(false);
const regenerationStatus = ref(null);
const regenerationResults = ref(null);
const step = ref(1); // 1: Check, 2: Warning, 3: Confirm, 4: Processing, 5: Complete

const CONFIRMATION_TEXT = 'REGENERATE ALL WALLETS';

const isConfirmValid = computed(() => {
    return confirmText.value === CONFIRMATION_TEXT && acknowledgeRisks.value;
});

watch(() => props.show, async (newValue) => {
    if (newValue) {
        resetModal();
        await checkRegenerationNeeded();
    }
});

const resetModal = () => {
    step.value = 1;
    confirmText.value = '';
    acknowledgeRisks.value = false;
    regenerationStatus.value = null;
    regenerationResults.value = null;
};

const checkRegenerationNeeded = async () => {
    checkingStatus.value = true;
    try {
        const response = await fetch('/admin/tron-wallets/check-regeneration', {
            headers: {
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
            }
        });
        
        const data = await response.json();
        
        if (data.success) {
            regenerationStatus.value = data;
            
            if (data.needs_regeneration) {
                step.value = 2; // Show warning
            } else {
                step.value = 6; // Show "not needed" message
            }
        } else {
            error(data.message || 'Failed to check regeneration status');
            close();
        }
    } catch (err) {
        error('Failed to check regeneration status. Please try again.');
        console.error(err);
        close();
    } finally {
        checkingStatus.value = false;
    }
};

const proceedToConfirmation = () => {
    step.value = 3;
};

const close = () => {
    if (!loading.value) {
        resetModal();
        emit('close');
    }
};

const executeRegeneration = async () => {
    if (!isConfirmValid.value) {
        error('Please confirm by typing the exact phrase and acknowledging the risks');
        return;
    }

    loading.value = true;
    step.value = 4; // Processing
    
    try {
        const response = await fetch('/admin/tron-wallets/regenerate', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
            }
        });
        
        const data = await response.json();
        
        if (data.success) {
            regenerationResults.value = data.results;
            step.value = 5; // Complete
            success(`✅ ${data.message}`);
            emit('regenerated', data.results);
        } else {
            error(data.message || 'Failed to regenerate wallets');
            step.value = 3; // Back to confirmation
        }
    } catch (err) {
        error('Failed to regenerate wallets. Please try again.');
        console.error(err);
        step.value = 3; // Back to confirmation
    } finally {
        loading.value = false;
    }
};

const closeAfterCompletion = () => {
    close();
    // Optionally reload the page to show updated addresses
    window.location.reload();
};
</script>

<template>
    <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto" @click.self="close">
        <div class="flex min-h-screen items-center justify-center p-4">
            <!-- Backdrop -->
            <div class="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" @click="close"></div>
            
            <!-- Modal -->
            <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl transform transition-all">
                <!-- Header -->
                <div class="relative px-8 pt-8 pb-6 border-b border-gray-200 dark:border-gray-700">
                    <div class="flex items-start justify-between">
                        <div class="flex items-center gap-4">
                            <div class="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <div>
                                <h3 class="text-2xl font-bold text-gray-900 dark:text-white">
                                    Wallet Regeneration System
                                </h3>
                                <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                    Master Seed Change Management
                                </p>
                            </div>
                        </div>
                        <button 
                            v-if="!loading"
                            @click="close" 
                            class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        >
                            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                <!-- Content -->
                <div class="px-8 py-6">
                    <!-- Step 1: Checking -->
                    <div v-if="step === 1" class="text-center py-12">
                        <div class="inline-flex items-center justify-center w-20 h-20 rounded-full bg-blue-100 dark:bg-blue-900/30 mb-6">
                            <svg class="animate-spin w-10 h-10 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                        <h4 class="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                            Checking System Status
                        </h4>
                        <p class="text-gray-600 dark:text-gray-400">
                            Verifying if wallet regeneration is required...
                        </p>
                    </div>

                    <!-- Step 2: Warning -->
                    <div v-if="step === 2" class="space-y-6">
                        <!-- Alert Banner -->
                        <div class="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-l-4 border-amber-500 rounded-lg p-6">
                            <div class="flex items-start gap-3">
                                <svg class="w-6 h-6 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                <div class="flex-1">
                                    <h5 class="font-bold text-amber-900 dark:text-amber-300 text-lg mb-2">
                                        ⚠️ Master Seed Change Detected
                                    </h5>
                                    <p class="text-amber-800 dark:text-amber-200 text-sm leading-relaxed">
                                        The system has detected that your master seed has been changed. All user wallet addresses 
                                        need to be regenerated to match the new seed.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- Status Info -->
                        <div v-if="regenerationStatus" class="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div class="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
                                <div class="text-xs text-gray-500 dark:text-gray-400 mb-1">Users (all roles except admin)</div>
                                <div class="text-lg font-semibold text-gray-900 dark:text-white">
                                    {{ regenerationStatus.total_users ?? '—' }}
                                </div>
                            </div>
                            <div class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                                <div class="text-xs text-blue-600 dark:text-blue-300 mb-1">Wallets with TRON address</div>
                                <div class="text-lg font-semibold text-blue-900 dark:text-blue-100">
                                    {{ regenerationStatus.users_with_wallets ?? regenerationStatus.wallets_with_addresses ?? '—' }}
                                </div>
                            </div>
                            <div class="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                                <div class="text-xs text-red-500 dark:text-red-400 mb-1">Sample check (for detection)</div>
                                <div class="text-sm font-semibold text-red-700 dark:text-red-300">
                                    User ID: {{ regenerationStatus.sample_user_id ?? 'N/A' }}
                                </div>
                                <div class="text-xs text-red-600 dark:text-red-300 mt-1">Example only — regeneration impacts all wallets</div>
                            </div>
                        </div>

                        <!-- Important Information -->
                        <div class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 space-y-3">
                            <h6 class="font-semibold text-blue-900 dark:text-blue-300 flex items-center gap-2">
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                What This Process Does
                            </h6>
                            <ul class="space-y-2 text-sm text-blue-800 dark:text-blue-200">
                                <li class="flex items-start gap-2">
                                    <svg class="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>Regenerates TRON wallet addresses for all users using the new master seed</span>
                                </li>
                                <li class="flex items-start gap-2">
                                    <svg class="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>Maintains the same derivation index for each user</span>
                                </li>
                                <li class="flex items-start gap-2">
                                    <svg class="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>Updates both user records and wallet tables automatically</span>
                                </li>
                                <li class="flex items-start gap-2">
                                    <svg class="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>Uses database transactions to ensure data integrity</span>
                                </li>
                            </ul>
                        </div>

                        <!-- Critical Warnings -->
                        <div class="bg-red-50 dark:bg-red-900/20 rounded-lg p-6 space-y-3 border border-red-200 dark:border-red-800">
                            <h6 class="font-semibold text-red-900 dark:text-red-300 flex items-center gap-2">
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                ⚠️ CRITICAL WARNINGS
                            </h6>
                            <ul class="space-y-2 text-sm text-red-800 dark:text-red-200">
                                <li class="flex items-start gap-2">
                                    <span class="font-bold">⚠️</span>
                                    <span><strong>Old addresses will no longer receive funds:</strong> Users should not send funds to their old addresses</span>
                                </li>
                                <li class="flex items-start gap-2">
                                    <span class="font-bold">⚠️</span>
                                    <span><strong>Active deposits:</strong> Ensure no pending deposits are using old addresses</span>
                                </li>
                                <li class="flex items-start gap-2">
                                    <span class="font-bold">⚠️</span>
                                    <span><strong>User notification:</strong> You must inform users about their new wallet addresses</span>
                                </li>
                                <li class="flex items-start gap-2">
                                    <span class="font-bold">⚠️</span>
                                    <span><strong>Backup required:</strong> Ensure old addresses with balances are backed up</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <!-- Step 3: Confirmation -->
                    <div v-if="step === 3" class="space-y-6">
                        <div class="text-center mb-6">
                            <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
                                <svg class="w-9 h-9 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                            </div>
                            <h4 class="text-xl font-bold text-gray-900 dark:text-white mb-2">
                                Final Confirmation Required
                            </h4>
                            <p class="text-gray-600 dark:text-gray-400">
                                This action will regenerate ALL user wallets
                            </p>
                        </div>

                        <!-- Confirmation Input -->
                        <div class="space-y-4">
                            <div>
                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                    Type "<span class="font-mono text-red-600 dark:text-red-400">{{ CONFIRMATION_TEXT }}</span>" to confirm:
                                </label>
                                <input
                                    v-model="confirmText"
                                    type="text"
                                    class="w-full px-4 py-3 border-2 rounded-lg bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:border-red-500 dark:focus:border-red-400 focus:ring-2 focus:ring-red-500/20 transition-all font-mono"
                                    :class="{ 'border-red-500 dark:border-red-400': confirmText && confirmText !== CONFIRMATION_TEXT }"
                                    placeholder="Type the confirmation text"
                                    autocomplete="off"
                                />
                            </div>

                            <!-- Acknowledgment Checkbox -->
                            <label class="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors">
                                <input
                                    v-model="acknowledgeRisks"
                                    type="checkbox"
                                    class="mt-1 w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-red-600 focus:ring-red-500 focus:ring-offset-0"
                                />
                                <span class="flex-1 text-sm text-gray-700 dark:text-gray-300">
                                    <strong class="font-semibold">I understand the risks and consequences</strong> of regenerating all user wallets. 
                                    I have backed up necessary data, verified there are no pending deposits, and am prepared to notify all users 
                                    about their new wallet addresses.
                                </span>
                            </label>
                        </div>

                        <!-- Progress Info -->
                        <div class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                            <div class="flex items-start gap-3">
                                <svg class="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <p class="text-sm text-blue-800 dark:text-blue-200">
                                    The regeneration process may take a few minutes depending on the number of users. 
                                    Do not close this window until the process is complete.
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Step 4: Processing -->
                    <div v-if="step === 4" class="text-center py-12">
                        <div class="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 mb-6 animate-pulse">
                            <svg class="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                        </div>
                        <h4 class="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                            Regenerating Wallets...
                        </h4>
                        <p class="text-gray-600 dark:text-gray-400 mb-6">
                            Please wait while we regenerate all user wallet addresses
                        </p>
                        <div class="flex items-center justify-center gap-2">
                            <div class="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style="animation-delay: 0ms"></div>
                            <div class="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style="animation-delay: 150ms"></div>
                            <div class="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style="animation-delay: 300ms"></div>
                        </div>
                        <p class="text-sm text-gray-500 dark:text-gray-400 mt-6">
                            ⚠️ Do not close this window
                        </p>
                    </div>

                    <!-- Step 5: Complete -->
                    <div v-if="step === 5" class="text-center py-8">
                        <div class="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 mb-6">
                            <svg class="w-10 h-10 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h4 class="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                            ✅ Regeneration Complete!
                        </h4>
                        <p class="text-gray-600 dark:text-gray-400 mb-6">
                            All user wallets have been successfully regenerated
                        </p>

                        <!-- Results -->
                        <div v-if="regenerationResults" class="grid grid-cols-3 gap-4 max-w-lg mx-auto mb-6">
                            <div class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                                <div class="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-1">
                                    {{ regenerationResults.total }}
                                </div>
                                <div class="text-xs text-blue-700 dark:text-blue-300 font-medium">
                                    Total Users
                                </div>
                            </div>
                            <div class="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                                <div class="text-3xl font-bold text-green-600 dark:text-green-400 mb-1">
                                    {{ regenerationResults.success }}
                                </div>
                                <div class="text-xs text-green-700 dark:text-green-300 font-medium">
                                    Successful
                                </div>
                            </div>
                            <div class="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                                <div class="text-3xl font-bold text-red-600 dark:text-red-400 mb-1">
                                    {{ regenerationResults.failed }}
                                </div>
                                <div class="text-xs text-red-700 dark:text-red-300 font-medium">
                                    Failed
                                </div>
                            </div>
                        </div>

                        <!-- Next Steps -->
                        <div class="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-6 text-left">
                            <h6 class="font-semibold text-amber-900 dark:text-amber-300 mb-3 flex items-center gap-2">
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                                </svg>
                                Next Steps
                            </h6>
                            <ul class="space-y-2 text-sm text-amber-800 dark:text-amber-200">
                                <li class="flex items-start gap-2">
                                    <span class="font-bold">1.</span>
                                    <span>Notify all users about their new TRON wallet addresses</span>
                                </li>
                                <li class="flex items-start gap-2">
                                    <span class="font-bold">2.</span>
                                    <span>Update any external systems that reference old addresses</span>
                                </li>
                                <li class="flex items-start gap-2">
                                    <span class="font-bold">3.</span>
                                    <span>Monitor the monitoring system to ensure deposits work correctly</span>
                                </li>
                                <li class="flex items-start gap-2">
                                    <span class="font-bold">4.</span>
                                    <span>Keep backup of old master seed if needed to recover old addresses</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <!-- Step 6: Not Needed -->
                    <div v-if="step === 6" class="text-center py-12">
                        <div class="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 mb-6">
                            <svg class="w-10 h-10 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h4 class="text-xl font-bold text-gray-900 dark:text-white mb-3">
                            ✅ No Regeneration Needed
                        </h4>
                        <p class="text-gray-600 dark:text-gray-400">
                            All user wallets are already synchronized with the current master seed.
                        </p>
                    </div>
                </div>

                <!-- Footer -->
                <div class="px-8 py-6 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700 rounded-b-2xl flex justify-end gap-3">
                    <!-- Step 2 buttons -->
                    <template v-if="step === 2">
                        <button
                            @click="close"
                            class="px-6 py-2.5 rounded-lg border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
                        >
                            Cancel
                        </button>
                        <button
                            @click="proceedToConfirmation"
                            class="px-6 py-2.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold hover:from-amber-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl"
                        >
                            I Understand, Continue →
                        </button>
                    </template>

                    <!-- Step 3 buttons -->
                    <template v-if="step === 3">
                        <button
                            @click="step = 2"
                            :disabled="loading"
                            class="px-6 py-2.5 rounded-lg border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold hover:bg-gray-100 dark:hover:bg-gray-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            ← Back
                        </button>
                        <button
                            @click="executeRegeneration"
                            :disabled="!isConfirmValid || loading"
                            class="px-6 py-2.5 rounded-lg bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold hover:from-red-600 hover:to-red-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg"
                        >
                            <span v-if="!loading">🔄 Regenerate All Wallets</span>
                            <span v-else class="flex items-center gap-2">
                                <svg class="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Processing...
                            </span>
                        </button>
                    </template>

                    <!-- Step 5 & 6 buttons -->
                    <template v-if="step === 5 || step === 6">
                        <button
                            @click="closeAfterCompletion"
                            class="px-8 py-2.5 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold hover:from-blue-600 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl"
                        >
                            Close & Refresh
                        </button>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
@keyframes bounce {
    0%, 100% {
        transform: translateY(0);
    }
    50% {
        transform: translateY(-10px);
    }
}

.animate-bounce {
    animation: bounce 1s infinite;
}
</style>
